import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, ArrowLeft } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const EASE = [0.16, 1, 0.3, 1];

const inputCls =
  "w-full rounded-xl border border-stone_warm bg-white px-4 py-3 text-sm text-charcoal placeholder-charcoal/35 outline-none transition-[border-color,box-shadow] duration-300 focus:border-gold focus:ring-2 focus:ring-gold/20";

export default function AdminLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotMessage, setForgotMessage] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);

  const handleForgot = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    setForgotMessage("");
    try {
      const res = await api.post("/auth/forgot-password", { email: forgotEmail });
      setForgotMessage(res.data.message);
    } catch (err) {
      setForgotMessage(formatApiError(err.response?.data?.detail, "Could not send the reset email. Please try again."));
    } finally {
      setForgotLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/login", { email, password });
      navigate("/admin");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail, "Login failed."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-ivory px-6" data-testid="admin-login-page">
      <motion.div
        initial={{ opacity: 0, y: 28 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, ease: EASE }}
        className="w-full max-w-md"
      >
        <div className="mb-10 text-center">
          <div className="mb-4 flex items-center justify-center gap-2.5">
            <span className="inline-block h-2 w-2 rotate-45 bg-gold" aria-hidden="true" />
            <span className="font-serif text-2xl font-semibold tracking-tight text-charcoal">Joseph Guild</span>
          </div>
          <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-charcoal/45">Guild office — administrator</p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="space-y-6 rounded-2xl border border-stone_warm bg-white p-8 shadow-[0_8px_30px_rgba(26,26,26,0.04)] sm:p-10"
          data-testid="admin-login-form"
        >
          <div>
            <label htmlFor="admin-email" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.24em] text-charcoal/55">
              Email
            </label>
            <input id="admin-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} placeholder="admin@josephguild.org" data-testid="admin-email-input" />
          </div>
          <div>
            <label htmlFor="admin-password" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.24em] text-charcoal/55">
              Password
            </label>
            <input id="admin-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} placeholder="••••••••" data-testid="admin-password-input" />
          </div>

          {error && (
            <p className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="admin-login-error">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={loading}
            className="inline-flex w-full items-center justify-center gap-3 rounded-full bg-charcoal px-8 py-4 font-mono text-[11px] uppercase tracking-[0.22em] text-ivory transition-colors duration-300 hover:bg-gold disabled:opacity-60"
            data-testid="admin-login-submit-button"
          >
            {loading && <Loader2 size={14} className="animate-spin" />}
            {loading ? "Signing in…" : "Sign in"}
          </button>

          <button
            type="button"
            onClick={() => { setShowForgot(!showForgot); setForgotMessage(""); }}
            className="w-full text-center font-mono text-[10px] uppercase tracking-[0.2em] text-charcoal/45 underline-offset-4 transition-colors hover:text-gold-dark hover:underline"
            data-testid="forgot-password-toggle"
          >
            Forgot your password?
          </button>
        </form>

        {showForgot && (
          <form onSubmit={handleForgot} className="mt-6 space-y-4 rounded-2xl border border-gold/40 bg-[#F3F0EB] p-7" data-testid="forgot-password-form">
            <p className="text-xs leading-relaxed text-charcoal/60">
              Enter the Guild office email and we will send a reset link to the administrator inbox.
            </p>
            <input
              type="email"
              required
              value={forgotEmail}
              onChange={(e) => setForgotEmail(e.target.value)}
              className={inputCls}
              placeholder="admin@josephguild.org"
              data-testid="forgot-email-input"
            />
            <button
              type="submit"
              disabled={forgotLoading}
              className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-charcoal bg-white px-6 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-charcoal transition-colors duration-300 hover:border-gold hover:text-gold-dark disabled:opacity-60"
              data-testid="forgot-submit-button"
            >
              {forgotLoading && <Loader2 size={12} className="animate-spin" />}
              {forgotLoading ? "Sending…" : "Send reset link"}
            </button>
            {forgotMessage && (
              <p className="text-xs leading-relaxed text-charcoal/70" data-testid="forgot-message">{forgotMessage}</p>
            )}
          </form>
        )}

        <Link
          to="/"
          className="mt-8 flex items-center justify-center gap-2 font-mono text-[10px] uppercase tracking-[0.24em] text-charcoal/45 transition-colors hover:text-gold-dark"
          data-testid="admin-back-home-link"
        >
          <ArrowLeft size={12} /> Back to the Guild
        </Link>
      </motion.div>
    </div>
  );
}
